import React, { useCallback, useEffect } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';

import t from 'utils/translate';
import { init } from 'admin/actions/init';
import { useAdminDispatch, useAdminSelector } from 'admin/reducers';
import { selectPasswordUpdatedAt } from 'admin/reducers/user/selectors';
import { isPasswordExpired } from 'admin/utils/password-expiry';
import Text from 'admin/components/common/Text';
import LoginLayout from 'admin/components/common/LoginLayout';
import { isMatching, QUERY } from 'admin/components/common/Responsive';
import ChangePasswordForm from 'admin/components/common/ChangePasswordForm';
import { LOGIN_PAGE, ROOT } from 'admin/constants/routes';

import css from './PasswordExpired.scss';

type Props = RouteComponentProps;

const PasswordExpired: React.FC<Props> = () => {
	const dispatch = useAdminDispatch();
	const history = useHistory();
	const isNoDesktop = isMatching(QUERY.NO_DESKTOP);
	const passwordUpdatedAt = useAdminSelector(selectPasswordUpdatedAt);

	// Redirect to main page if password is not expired
	useEffect(() => {
		if (!isPasswordExpired(passwordUpdatedAt)) {
			history.replace(ROOT);
		}
	}, [passwordUpdatedAt, history]);

	/**
	 * After successful password change, reinitialize the app to refresh user data.
	 * Redirect will happen automatically via PrivateRoute logic.
	 * */
	const onSuccess = useCallback(async () => {
		await dispatch(init(true));
		history.push(LOGIN_PAGE);
	}, [dispatch, history]);

	const renderContent = () => (
		<div className={css.inner}>
			<Text tag="h1" size={isNoDesktop ? 'subheading' : 'heading'}>
				{t('passwordExpired.title')}
			</Text>
			<Text tag="p" size="label" className={css.heroSubTitle} color={Text.color.blackDark}>
				{t('passwordExpired.subtitle')}
			</Text>

			<ChangePasswordForm onSuccess={onSuccess} showCancelButton={false} />
		</div>
	);

	return <LoginLayout contentClassName={css.layoutContent}>{renderContent()}</LoginLayout>;
};

export default PasswordExpired;
